// Frontend/src/components/CommissionBreakdown.tsx
/**
 * Commission breakdown component for invoice payment flow
 * Shows platform commission and net amount paid to HOA
 */

import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Receipt, TrendingDown, TrendingUp, CheckCircle2 } from "lucide-react";
import type { Invoice } from "@/api/payments";
import { useLanguage } from "@/contexts/LanguageContext";

interface CommissionBreakdownProps {
  invoice: Invoice;
  className?: string;
}

export function CommissionBreakdown({
  invoice,
  className = "",
}: CommissionBreakdownProps) {
  const { t } = useLanguage();
  const showCommissionBreakdown =
    invoice.payment_flow !== "DIRECT_HOA" &&
    (invoice.status === "PENDING_VERIFICATION" ||
      invoice.status === "PENDING_HOA_TRANSFER" ||
      invoice.status === "PAID" ||
      !!invoice.paid_to_platform);

  if (!showCommissionBreakdown) {
    return null;
  }

  const totalAmount = parseFloat(invoice.total_amount || "0");
  const commission = parseFloat(invoice.platform_commission_amount || "0");
  const hoaAmount = parseFloat(invoice.paid_to_hoa_amount || "0");

  return (
    <Card className={`rounded-2xl p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Receipt className="w-5 h-5 text-[#1F3A93]" />
        <h3 className="text-lg font-semibold text-gray-900">
          {t("commission.title")}
        </h3>
      </div>

      <div className="space-y-4">
        {/* Total Amount */}
        <div className="flex justify-between items-center pb-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t("commission.totalAmount")}</span>
          </div>
          <span className="text-lg font-bold text-gray-900">
            ${totalAmount.toFixed(2)}
          </span>
        </div>

        {/* Platform Commission */}
        {commission > 0 && (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-600">{t("commission.platformFee")}</span>
              {invoice.paid_to_platform && (
                <Badge variant="secondary" className="rounded-lg text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {t("commission.paid")}
                </Badge>
              )}
            </div>
            <span className="text-base font-semibold text-orange-600">
              -${commission.toFixed(2)}
            </span>
          </div>
        )}

        {/* HOA Net Amount */}
        {hoaAmount > 0 && (
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-gray-700">
                {t("commission.netToHoa")}
              </span>
              {invoice.status === "PAID" && (
                <Badge variant="default" className="rounded-lg text-xs bg-green-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {t("commission.paid")}
                </Badge>
              )}
            </div>
            <span className="text-lg font-bold text-green-600">
              ${hoaAmount.toFixed(2)}
            </span>
          </div>
        )}

        {/* Status Info */}
        {invoice.paid_to_platform && invoice.status !== "PAID" && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              {t("commission.pendingTransfer")}
            </p>
          </div>
        )}

        {invoice.status === "PAID" && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-700">
              {t("commission.completedCycle")}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
